/**
 * TranscriptPanel — renders an ordered list of canonical transcript segments as
 * speaker-labelled bubbles. Provider-independent: it reads only the canonical
 * `TranscriptSegment` fields and never a raw provider payload.
 *
 * Live updates arrive through the TanStack Query cache: realtime cache handlers
 * (registered by `RealtimeProvider`) patch the `callTranscript` query, so this
 * panel re-renders automatically as `call.transcript.final` events land. The
 * panel itself does not subscribe — the page owns the room subscription.
 */

import type { TranscriptSegment } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import { cn, EmptyState } from '@hostesa/ui';

export interface TranscriptPanelProps {
  segments: readonly TranscriptSegment[];
  'data-testid'?: string;
}

/** Canonical speaker → translation key + alignment. */
function speakerKey(speaker: TranscriptSegment['speaker']): string {
  return `speaker.${speaker}`;
}

export function TranscriptPanel({ segments, 'data-testid': testId }: TranscriptPanelProps) {
  const { t } = useT('calls');

  if (segments.length === 0) {
    return (
      <EmptyState
        data-testid={`${testId ?? 'transcript-panel'}-empty`}
        title={t('detail.transcript.empty.title')}
        description={t('detail.transcript.empty.description')}
      />
    );
  }

  const ordered = [...segments].sort((a, b) => a.seq - b.seq);

  return (
    <ol data-testid={testId ?? 'transcript-panel'} className="flex flex-col gap-3">
      {ordered.map((segment) => {
        const isGuest = segment.speaker === 'guest';
        return (
          <li
            key={segment.id}
            data-testid={`transcript-segment-${segment.id}`}
            data-speaker={segment.speaker}
            className={cn('flex flex-col gap-1', isGuest ? 'items-start' : 'items-end')}
          >
            <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-stone-mid)]">
              {t(speakerKey(segment.speaker))}
            </span>
            <p
              className={cn(
                'max-w-[36ch] rounded-lg px-3 py-2 text-sm leading-relaxed',
                isGuest
                  ? 'bg-[var(--color-stone-100)] text-[var(--color-charcoal)]'
                  : 'bg-[var(--color-warm-stone-soft)] text-[var(--color-charcoal)]',
              )}
            >
              {segment.text}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
